import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  Signal,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterOutlet } from '@angular/router';
import { HeaderComponent } from '@app/_shared/components/header/header.component';
import { MessagesComponent } from '@app/_shared/widgets/messages/messages.component';
import { selectUserPlan } from '@app/_store/features/user-plan/user-plan.selectors';
import { selectUserData } from '@app/_store/selectors/global.selectors';
import { NavigationStateKeys } from '@core/configs/navigation.config';
import { Navigation } from '@core/models/interfaces/navigation';
import { UserPlan } from '@core/models/interfaces/storage-data/user-plan';
import { UserDataType } from '@core/models/types/user-data.type';
import { NavigationService } from '@core/services/navigation.service';
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-dense-layout',
  templateUrl: './dense.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FuseVerticalNavigationComponent,
    MatButtonModule,
    MatIconModule,
    RouterOutlet,
    HeaderComponent,
    MessagesComponent,
    NgClass,
  ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
  public navigationService = inject(NavigationService);
  private store = inject(Store);
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  private _fuseNavigationService = inject(FuseNavigationService);

  /**
   * R36. Signals rather than plain fields: each is written from a `subscribe`, and such a write
   * marks nothing dirty on its own — the zone is the only reason the shell repaints today. Free
   * under zone.js, and a precondition for ever going zoneless.
   */
  readonly isScreenSmall = signal(false);
  readonly navigation = signal<Navigation | null>(null);
  readonly navigationAppearance = signal<'default' | 'dense'>('dense');
  readonly isOpened = signal(false);
  readonly isHovered = signal(false);
  readonly isStatic = signal(false);
  readonly userPlan = signal<UserPlan['object'] | null>(null);
  public user: Signal<UserDataType | undefined> = toSignal(
    this.store.select(selectUserData)
  );
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  get appState(): NavigationStateKeys {
    return this.navigationService.appStateKey;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this.navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation.set(navigation);
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall.set(!matchingAliases.includes('md'));

        // Change the navigation appearance
        this.navigationAppearance.set(
          this.isScreenSmall() ? 'default' : 'dense'
        );
      });

    this.store
      .select(selectUserPlan)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((userPlan) => {
        this.userPlan.set(userPlan);
      });

    this.renderer.addClass(
      this.elementRef.nativeElement,
      `bg-${this.appState}`
    );
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  /**
   * Toggle the navigation appearance
   */
  toggleNavigationAppearance(): void {
    this.navigationAppearance.set(
      this.navigationAppearance() === 'default' ? 'dense' : 'default'
    );
  }

  onWidgetOpened(opened: boolean): void {
    this.isStatic.set(opened);

    if (this.isScreenSmall() && this.isOpened()) {
      this.toggleNavigation('mainNavigation');
    }
  }
}
